import { ROUTES } from "../../constants/routes";
import { Component, computed, inject, signal } from "@angular/core";
import { RouterLink } from "@angular/router";
import { Navbar } from "../../components/navbar/navbar";
import { Breadcrumb } from "../../components/breadcrumb/breadcrumb";
import { Footer } from "../../components/footer/footer";
import { SeoService } from "../../services/seo.service";
import { PRICING } from "../../constants/site";
import {
  breadcrumbSchema,
  faqSchema,
  offerCatalogSchema,
  webPageSchema
} from "../../helpers/structured-data";

@Component({
  selector: "app-pricing",
  imports: [RouterLink, Breadcrumb, Navbar, Footer],
  templateUrl: "./pricing.html",
  styleUrl: "./pricing.scss"
})
export class Pricing {
  readonly routes = ROUTES;
  private readonly seo = inject(SeoService);
  readonly pricing = PRICING;

  readonly volume = signal(50000);

  readonly rate = computed(() =>
    this.volume() >= PRICING.bulkThreshold ? PRICING.bulkPerRecord : PRICING.standardPerRecord
  );

  readonly monthlyCost = computed(() => this.volume() * this.rate());

  readonly isBulk = computed(() => this.volume() >= PRICING.bulkThreshold);

  included = [
    "Confirmed date of death and age at death",
    "Last known property and mailing address",
    "Match confidence score on every result",
    "Nationwide coverage across all 50 states",
    "Weekly data refresh, often within seven days",
    "Batch file upload and real time API access",
    "No contracts, no minimums, no setup fees",
    "Pay only for the records we confirm"
  ];

  examples = [
    { volume: "10,000", rate: "$0.04", cost: "$400" },
    { volume: "50,000", rate: "$0.04", cost: "$2,000" },
    { volume: "100,000", rate: "$0.01", cost: "$1,000" },
    { volume: "500,000", rate: "$0.01", cost: "$5,000" },
    { volume: "1,000,000", rate: "$0.01", cost: "$10,000" }
  ];

  faqs = [
    {
      question: "How does usage based pricing work?",
      answer:
        "You pay per record you verify. Standard verification is $0.04 per record. When you verify at least 100,000 records in a month, bulk pricing of $0.01 per record applies. There are no contracts or minimum commitments."
    },
    {
      question: "Do I pay for records that are not matched?",
      answer:
        "You only pay for records we confirm as deceased. Records that return no match are not billed, so you never pay to learn that a person is alive."
    },
    {
      question: "When does bulk pricing apply?",
      answer:
        "Bulk pricing of $0.01 per record applies once you verify 100,000 or more records in a calendar month. This is ideal for banks, card issuers and enterprises running large portfolio checks."
    },
    {
      question: "What is free, exactly?",
      answer:
        "Creating an account is free and no credit card is required. There is no subscription, no setup fee, no minimum and no contract, so an account costs you nothing until you run records. You add funds when you are ready, and you are charged $0.04 per confirmed match. Records we cannot confirm as deceased are never billed."
    },
    {
      question: "Is there a free trial or a free credit?",
      answer:
        "There is no fixed free credit. Because you are only charged for confirmed matches, you can create an account and run a small sample list to check data quality and match rate, and you will only pay for the deaths we actually verify. If you want a larger structured pilot, contact sales and we will set one up."
    },
    {
      question: "Do you offer enterprise and API pricing?",
      answer:
        "Yes. High volume customers and API integrations can get custom pricing, dedicated support and volume commitments. Contact our sales team to build a plan around your needs."
    },
    {
      question: "Can I get a refund if I am billed incorrectly?",
      answer:
        "Yes. Raise it with us within 60 days of the charge and we will investigate. We credit or refund any charge that is not valid, including records billed as confirmed matches that were not, duplicate charges within a batch, charges applied after you closed your account, and anything caused by an error on our side. Approved refunds go back to the original payment method or your account balance, normally within ten business days."
    },
    {
      question: "How do I cancel, and is there a cancellation fee?",
      answer:
        "There is no minimum term, no notice period and no cancellation fee. Close your account from account settings or email support and billing stops immediately, your API keys are revoked and any unused wallet balance can be refunded on request. Download the result files you still need before you close."
    },
    {
      question: "What if we disagree about a specific match?",
      answer:
        "Tell us which record and we will review the underlying data. Where we agree the match was in error, that record is refunded. We would always rather resolve it directly than have you go through a card chargeback, which is slower for both of us."
    }
  ];

  setVolume(value: string): void {
    this.volume.set(Number(value));
  }

  ngOnInit(): void {
    this.seo.apply({
      title: "Pricing from $0.01 per Verified Record",
      description:
        "Pay $0.04 per verified record, or $0.01 with bulk pricing above 100,000 records a month. No contracts, no minimums, and you only pay for confirmed matches.",
      path: "/pricing",
      keywords:
        "death verification pricing, deceased verification cost, per record death data pricing, bulk death verification, death screening API pricing",
      structuredData: [
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Pricing", path: "/pricing" }
        ]),
        webPageSchema({
          name: "VerifyDeceased pricing",
          description:
            "Usage based pricing for deceased consumer verification, from $0.01 per confirmed record.",
          path: "/pricing"
        }),
        offerCatalogSchema(),
        faqSchema(this.faqs)
      ]
    });
  }
}
